import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Col, Spinner } from 'react-bootstrap'
import PrimaryButton from '../buttons/PrimaryButton'
import theme from '../../constants/theme'
import {
  CompanyInfoText,
  ProfileSectionItemCardWrapper,
  ProfileSectionItemHeader,
  JobItem,
  JobItemDetails,
  JobCardWrapper,
  JobDetailsLink,
  JobProfileSectionItemDurationText,
  JobNavigation,
  JobTitle,
} from './styles'
import ProfileSectionItemModal from './ProfileSectionModal'
import Trash from '../../assets/svgs/trash'
import Plus from '../../assets/svgs/plus'
import PencilSquare from '../../assets/svgs/PencilSquare'

const ProfileSection = ({
  handleUpdate,
  handlePrevTab,
  handleNextTab,
  responseProfileSectionItems = [],
  isSubmit = false,
  objectKey = '',
}) => {
  const { t } = useTranslation()
  const [profileSectionItems, setProfileSectionItems] = useState(
    responseProfileSectionItems?.map((exp) => ({ ...exp })) || [],
  )
  const [selectedProfileSectionItem, setSelectedProfileSectionItem] =
    useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [triggerChange, setTriggerChange] = useState(false)

  useEffect(() => {
    setProfileSectionItems((prev) => prev?.map((exp) => ({ ...exp })))
  }, [triggerChange])

  useEffect(() => {
    setProfileSectionItems([...responseProfileSectionItems])
  }, [objectKey])

  useEffect(() => {
    handleUpdate(profileSectionItems)
  }, [profileSectionItems])

  const handleEdit = (profileSectionItem) => {
    setSelectedProfileSectionItem(profileSectionItem)
    setShowModal(true)
    setIsEdit(true)
  }

  const handleAdd = () => {
    setShowModal(true)
    setIsEdit(false)
  }

  const handleDelete = (profileSectionItem) => {
    setProfileSectionItems(
      profileSectionItems.filter((exp) => exp.id !== profileSectionItem.id),
    )
  }

  const getUniqueRandomId = () => {
    let id
    do {
      id = Math.floor(Math.random() * 1000000)
    } while (profileSectionItems.some((exp) => exp.id === id))
    return id
  }

  const handleSave = (profileSectionItem) => {
    if (isEdit) {
      const index = profileSectionItems.findIndex(
        (exp) => exp.id === profileSectionItem.id,
      )
      if (index >= 0) {
        const newProfileSectionItems = [...profileSectionItems]
        newProfileSectionItems[index] = profileSectionItem
        setProfileSectionItems(newProfileSectionItems)
      }
      setSelectedProfileSectionItem(null)
    } else {
      if (
        !profileSectionItems.some((exp) => exp.id === profileSectionItem.id)
      ) {
        setProfileSectionItems([
          ...profileSectionItems,
          { id: getUniqueRandomId(), ...profileSectionItem, isNew: true },
        ])
      }
    }
  }

  return (
    <>
      <ProfileSectionItemModal
        defaultValues={selectedProfileSectionItem}
        show={showModal}
        setShow={setShowModal}
        isEdit={isEdit}
        handleSubmit={handleSave}
        objectKey={objectKey}
        triggerChange={setTriggerChange}
      />
      <ProfileSectionItemCardWrapper>
        <ProfileSectionItemHeader>
          <Col className="text-capitalize">{t(objectKey)}</Col>
        </ProfileSectionItemHeader>
        <PrimaryButton
          type="submit"
          variant="outline-light"
          onClick={handleAdd}
        >
          <Plus width="16" height="16" className="me-2" /> {t('general.add')}{' '}
          {t(objectKey)}
        </PrimaryButton>
      </ProfileSectionItemCardWrapper>

      <JobCardWrapper>
        {profileSectionItems?.map((item) => (
          <ProfileSectionItem
            key={item.id}
            profileItem={item}
            onEdit={handleEdit}
            onDelete={handleDelete}
            objectKey={objectKey}
          />
        ))}

        <JobNavigation>
          <NavigationButton
            type="prev"
            onClick={handlePrevTab}
            disabled={isLoading}
          />
          <NavigationButton
            type={isSubmit ? 'submit' : 'next'}
            onClick={() => {
              if (isSubmit) setIsLoading(true)
              handleNextTab()
            }}
            disabled={isLoading}
            isSubmit={isSubmit}
            isLoading={isLoading}
          />
        </JobNavigation>
      </JobCardWrapper>
    </>
  )
}

ProfileSection.propTypes = {
  responseProfileSectionItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      company: PropTypes.string,
      duration: PropTypes.string,
    }),
  ),
  handleUpdate: PropTypes.func.isRequired,
  handlePrevTab: PropTypes.func.isRequired,
  handleNextTab: PropTypes.func.isRequired,
  isSubmit: PropTypes.bool,
  objectKey: PropTypes.string,
}

export default ProfileSection

const ProfileSectionItem = ({
  profileItem,
  onEdit,
  onDelete,
  objectKey = '',
}) => {
  const getKeys = () => {
    switch (objectKey) {
      case 'experience':
        return {
          titleKey: 'position',
          instituteKey: 'company',
          startDateKey: 'startDate',
          endDateKey: 'endDate',
        }
      case 'education':
        return {
          titleKey: 'fieldOfStudy',
          instituteKey: 'institute',
          startDateKey: 'startDate',
          endDateKey: 'endDate',
        }
      default:
        return {
          titleKey: '',
          instituteKey: '',
          startDateKey: '',
          endDateKey: '',
        }
    }
  }

  const getDateDiff = (startDate, endDate) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffTime = Math.abs(end - start)
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24))
    const diffMonths = Math.round(diffTime / (1000 * 60 * 60 * 24 * 30))
    const diffYears = Math.round(diffTime / (1000 * 60 * 60 * 24 * 30 * 12))

    if (diffYears > 0) {
      return `${diffYears} years`
    }
    if (diffMonths > 0) {
      return `${diffMonths} months`
    }
    return `${diffDays} days`
  }

  const getDuration = (startDate, endDate) => {
    let duration = ''

    if (startDate && endDate) {
      duration += `${startDate} - ${endDate}, ${getDateDiff(
        startDate,
        endDate,
      )}`
    } else if (startDate) {
      duration += `${startDate} - present, ${getDateDiff(
        startDate,
        new Date(),
      )}`
    } else {
      duration += 'currently working'
    }

    return duration
  }

  return (
    <JobItem>
      <JobItemDetails>
        <JobTitle>{profileItem[getKeys().titleKey]}</JobTitle>
        <CompanyInfoText>{profileItem[getKeys().instituteKey]}</CompanyInfoText>
        <JobProfileSectionItemDurationText>
          {getDuration(profileItem.startDate, profileItem.endDate)}
        </JobProfileSectionItemDurationText>
      </JobItemDetails>
      <JobDetailsLink
        variant="light"
        color="#a93c38"
        hovercolor="#6f2725"
        onClick={() => onDelete(profileItem)}
      >
        <Trash width="16" height="16" />
      </JobDetailsLink>
      <JobDetailsLink variant="light" onClick={() => onEdit(profileItem)}>
        <PencilSquare width="16" height="16" />
      </JobDetailsLink>
    </JobItem>
  )
}
ProfileSectionItem.propTypes = {
  profileItem: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    company: PropTypes.string,
    duration: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  objectKey: PropTypes.string,
}

const NavigationButton = ({
  type,
  onClick,
  disabled,
  isSubmit = false,
  isLoading = false,
}) => {
  const { t } = useTranslation()
  const themeColor =
    type === 'submit' ? theme.success : theme.talentinoSpecialBlue

  const buttonProps = {
    backgroundcolor: type === 'prev' ? 'transparent' : themeColor,
    color: type === 'prev' ? theme.talentinoSpecialBlue : undefined,
    hovercolor: type === 'prev' ? theme.talentinoSpecialDarkBlue : undefined,
    type: 'submit',
    variant: type !== 'prev' ? 'outline-light' : undefined,
    onClick,
    disabled,
    className: type === 'prev' ? 'me-2' : undefined,
    border:
      type === 'prev' ? `1px solid ${theme.talentinoSpecialBlue}` : undefined,
  }

  return (
    <PrimaryButton {...buttonProps}>
      {isSubmit && isLoading && (
        <Spinner
          animation="border"
          variant="light"
          size="sm"
          className="me-2"
        />
      )}
      {t(type)}
    </PrimaryButton>
  )
}

NavigationButton.propTypes = {
  type: PropTypes.oneOf(['prev', 'next', 'submit']).isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isSubmit: PropTypes.bool,
  isLoading: PropTypes.bool,
}
